// Relatel and its Facebook SDK live in one removable document. No custom
// contact launcher: the vendor's own chat and the normal contact page remain.
export function mountConsentChat(host, scriptCode) {
  const raw = typeof scriptCode === 'string' ? scriptCode.trim() : '';
  const body = /<script/i.test(raw)
    ? (raw.match(/<script[^>]*>([\s\S]*?)<\/script>/i)?.[1] || '').trim() : raw;
  if (!body) return () => {};
  let frame, expanded = false, disposed = false, scheduled = 0;
  host.setAttribute('data-fof-chat-space', '');
  const position = () => {
    scheduled = 0;
    if (!frame || disposed) return;
    const viewport = window.visualViewport;
    const height = viewport?.height || window.innerHeight;
    const offset = viewport?.offsetTop || 0;
    const keyboard = Math.max(0, window.innerHeight - height - offset);
    frame.style.width = expanded ? 'min(390px, 100vw)' : '125px';
    // Leave room below the open chat for footer cookie controls. The native
    // minimize button and message input remain within the visual viewport.
    frame.style.height = expanded ? `${Math.max(140, Math.min(620, height - 80))}px` : '80px';
    frame.style.bottom = `${keyboard + (expanded ? 60 : 0)}px`;
    frame.hidden = false;
    if (expanded) return;
    if (document.activeElement?.matches('input,textarea,select,[contenteditable="true"]')) {
      frame.hidden = true; return;
    }
    const rect = frame.getBoundingClientRect();
    const obstacles = [...document.querySelectorAll('input,textarea,select,button,a,[role="button"],.goods-editor-footer,.mobile-wizard-actions,.step-actions,[class*="summary"]')]
      // Cookiebot's modal closes after its acceptance event. Its temporary
      // buttons must not leave the launcher hidden after the dialog disappears.
      .filter(el => !host.contains(el) && !el.closest('#CybotCookiebotDialog') && el.getClientRects().length)
      .map(el => el.getBoundingClientRect())
      .filter(r => r.width && r.height && r.left < rect.right + 8 && r.right > rect.left - 8 && r.bottom > 0 && r.top < height);
    let top = height - rect.height;
    for (let i = 0; i <= obstacles.length; i++) {
      const candidate = top;
      const collision = obstacles.filter(r => r.top < candidate + rect.height + 8 && r.bottom > candidate - 8);
      if (!collision.length) break;
      top = Math.min(...collision.map(r => r.top)) - rect.height - 8;
    }
    frame.hidden = top < 8;
    if (!frame.hidden) frame.style.bottom = `${Math.max(keyboard, window.innerHeight - top - rect.height)}px`;
  };
  function queuePosition() { if (!scheduled) scheduled = requestAnimationFrame(position); }
  const resize = event => {
    if (!frame || event.source !== frame.contentWindow || event.origin !== window.location.origin || event.data?.type !== 'fof-chat-size') return;
    expanded = event.data.expanded === true; position();
  };
  const synchronize = () => {
    if (disposed) return;
    if (window.Cookiebot?.consent?.marketing !== true) {
      frame?.remove(); frame = undefined; expanded = false; return;
    }
    if (frame) return;
    frame = document.createElement('iframe');
    frame.title = 'Kontakt via Relatel-chat'; frame.setAttribute('data-fof-consent-chat', '');
    Object.assign(frame.style, {position:'fixed',right:'0',bottom:'0',border:0,width:'125px',height:'80px',background:'transparent',zIndex:'900'});
    // Trusted CMS embed, not a sandbox for untrusted code. Validate both bridges.
    const serialized = JSON.stringify(body).replace(/</g, '\\u003c');
    // The wrapper supplies spacing; remove the embed's extra 20px offset so
    // its native 125x80 launcher fits between mobile form controls.
    frame.srcdoc = '<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0}[class*="utah__Container__container"]{right:0!important;bottom:0!important}</style></head><body><script>' +
      'window.addEventListener("message",function(e){if(e.origin!=="https://bank.relatel.dk")return;var f=document.querySelector("iframe");if(!f||e.source!==f.contentWindow)return;if(e.data&&/^(bank\\/MAXIMIZE|bank\\/MINIMIZE)$/.test(e.data.type))parent.postMessage({type:"fof-chat-size",expanded:e.data.type==="bank/MAXIMIZE"},' +
      JSON.stringify(window.location.origin) + ');});var s=document.createElement("script");s.textContent=' + serialized + ';document.body.appendChild(s);</script></body></html>';
    host.append(frame); position();
  };
  const events = ['CookiebotOnConsentReady','CookiebotOnAccept','CookiebotOnDecline'];
  events.forEach(name => window.addEventListener(name,synchronize));
  window.addEventListener('message',resize);
  window.addEventListener('scroll',queuePosition,{passive:true});
  window.addEventListener('resize',queuePosition);
  window.visualViewport?.addEventListener('resize',queuePosition);
  window.visualViewport?.addEventListener('scroll',queuePosition);
  document.addEventListener('focusin',queuePosition);
  document.addEventListener('focusout',queuePosition);
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(queuePosition);
  observer?.observe(document.body);
  synchronize();
  return () => {
    disposed = true; cancelAnimationFrame(scheduled); observer?.disconnect();
    events.forEach(name => window.removeEventListener(name,synchronize));
    window.removeEventListener('message',resize);
    window.removeEventListener('scroll',queuePosition);
    window.removeEventListener('resize',queuePosition);
    window.visualViewport?.removeEventListener('resize',queuePosition);
    window.visualViewport?.removeEventListener('scroll',queuePosition);
    document.removeEventListener('focusin',queuePosition);
    document.removeEventListener('focusout',queuePosition);
    frame?.remove(); host.removeAttribute('data-fof-chat-space');
  };
}
